import classNames from 'classnames';
import { Formik, FormikProps } from 'formik';
import React, {
  FC, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Column } from 'components/base/Column';

import { MasterDataQuestionType } from 'modules/budget/api/types';
import { BudgetLocationSelector } from 'modules/budget/components/articleSelectors/BudgetLocationSelector';
import { CitySelector } from 'modules/budget/components/commons/CitySelector';
import { Separator } from 'modules/budget/components/commons/Separator';
import { YesNoSelector } from 'modules/budget/components/commons/YesNoSelector';
import { Classable } from 'modules/budget/components/types/helpers';
import { BudgetQuestionCodes } from 'modules/budget/data/budgetQuestionCodes';
import { useBudget } from 'modules/budget/hooks/useBudget';
import { useQuestionLabels } from 'modules/budget/hooks/useQuestionLabels';
import { Address } from 'modules/budget/models/Address';
import { ILocation } from 'modules/budget/models/Location/types';
import { dismissServiceId } from 'modules/budget/store/data';
import { isBoolean, isBooleanAndFalse, isBooleanAndTrue } from 'utils/helpers';

export interface Q8Response {
  dismissService?: boolean;
  address: Address | null;
  location: ILocation | null;
  autorespond: boolean;
}

interface Q8FormComponent extends Classable {
  onChange: (value: Q8Response) => void;
  question: BudgetQuestionCodes;
}

export const Q8Form: FC<Q8FormComponent> = ({ onChange, className, question }) => {
  const { yes, no } = useQuestionLabels();
  const { t } = useTranslation();
  const { GetQuestionValue } = useBudget();
  const [wantService, setWantService] = useState<boolean>();
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<ILocation | null>(null);

  const storedValue = useMemo(
    () => GetQuestionValue(question),
    [GetQuestionValue, question],
  );

  useEffect(
    () => {
      if (storedValue?.address && storedValue?.location) {
        onChange({
          address: new Address(storedValue?.address),
          autorespond: false,
          dismissService: false,
          location: storedValue?.location,
        });
        setSelectedAddress(new Address(storedValue?.address));
        setSelectedLocation(storedValue?.location);
        setWantService(true);
      } else if (storedValue === dismissServiceId) {
        onChange({
          address: null,
          autorespond: false,
          dismissService: true,
          location: null,
        });
        setWantService(false);
        setSelectedAddress(null);
        setSelectedLocation(null);
      } else {
        setSelectedAddress(null);
        setSelectedLocation(null);
      }
    },
    [onChange, storedValue],
  );

  return (
    <Formik
      initialValues={{
        address: selectedAddress,
        autorespond: false,
        dismissService: isBooleanAndTrue(wantService) || isBooleanAndFalse(wantService)
          ? !wantService
          : undefined,
        location: selectedLocation,
      }}
      validate={(values) => {
        onChange({
          address: values.address,
          autorespond: values.autorespond,
          dismissService: values.dismissService,
          location: values.location,
        });
        setWantService(isBooleanAndTrue(values.dismissService)
        || isBooleanAndFalse(values.dismissService)
          ? !values.dismissService
          : undefined);
        setSelectedAddress(values.address);
        setSelectedLocation(values.location);

        return {};
      }}
      enableReinitialize
      validateOnBlur
      validateOnChange
      onSubmit={() => Promise.resolve()}
    >
      {({
        values, setValues,
      }: FormikProps<Q8Response>) => {
        const hideElements = values.dismissService === undefined
        || isBooleanAndTrue(values.dismissService);

        return (
          <Column className={classNames(className)}>
            <YesNoSelector
              noCaption={no(question)}
              value={isBooleanAndTrue(values.dismissService)
                || isBooleanAndFalse(values.dismissService)
                ? !values.dismissService
                : undefined}
              yesCaption={yes(question)}
              onChange={(v) => {
                setValues({
                  address: null,
                  autorespond: isBooleanAndFalse(v),
                  dismissService: isBoolean(v) ? !v : undefined,
                  location: null,
                });
              }}
            />
            <Separator />
            <CitySelector
              cityCaption={t('budget.wizard.city.interment')}
              cityCode={values.address?.city || ''}
              cityWork={Boolean(values.address?.work)}
              className={classNames({ 'f-budget-hidden': hideElements })}
              countryCaption={t('budget.wizard.country.interment')}
              countryCode={values.address?.country || ''}
              disabled={Boolean(values.dismissService)}
              provinceCaption={t('budget.wizard.province.interment')}
              provinceCode={values.address?.province || ''}
              emitPartial
              onChange={(val) => {
                setValues({
                  address: new Address(val),
                  autorespond: false,
                  dismissService: values.dismissService,
                  location: values.address?.isEqual(val) ? values.location : null,
                });
              }}
            />
            <Separator />
            <BudgetLocationSelector
              caption={t('service.interment.cemetery')}
              city={values.address?.city || ''}
              className={classNames({ 'f-budget-hidden': hideElements })}
              country={values.address?.country || ''}
              disabled={Boolean(values.dismissService)}
              province={values.address?.province || ''}
              questionType={MasterDataQuestionType.INTERMENT}
              value={values.location?.id || ''}
              onChange={(val) => {
                setValues({
                  address: values.address,
                  autorespond: false,
                  dismissService: values.dismissService,
                  location: val || null,
                });
              }}
            />
          </Column>
        );
      } }
    </Formik>
  );
};
